import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import '../styles/dashboard-header.css';

interface NavItem {
  label: string;
  path?: string;
  onClick?: () => void;
}

const DashboardHeader: React.FC = () => {
  const { user, logout } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [isNavScrolled, setIsNavScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Scroll handler for nav
  useEffect(() => {
    const handleScroll = () => {
      setIsNavScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setAccountDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getDashboardPath = () => {
    if (!user) return '/';
    switch (user.role) {
      case 'admin': return '/admin';
      case 'headmaster':
      case 'headteacher': return '/headteacher';
      case 'coach': return '/coach';
      default: return '/';
    }
  };

  const getNavItems = (): NavItem[] => {
    if (!user) return [];
    
    switch (user.role) {
      case 'coach':
        return [
          { label: 'Dashboard', path: '/coach' },
          { label: 'Register Student', path: '/register' },
          { label: 'Reports', path: '/reports' },
        ];
      
      case 'headmaster':
      case 'headteacher':
        return [
          { label: 'Dashboard', path: '/headteacher' },
          { label: 'New Registrations', path: '/new-registrations' },
          { label: 'Reports', path: '/reports' },
        ];
      
      case 'admin':
        return [
          { label: 'Dashboard', path: '/admin' },
          { label: 'Users', path: '/admin/users' },
          { label: 'Registrations', path: '/admin/registrations' },
          { label: 'Schools', path: '/admin/schools' },
        ];
      
      default:
        return [];
    }
  };

  // Get account path based on role
  const getAccountPath = () => {
    if (!user) return '/account';
    return user.role === 'admin' ? '/admin/account' : '/account';
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const navItems = getNavItems();

  return (
    <nav className={`dashboard-nav ${isNavScrolled ? 'scrolled' : ''}`}>
      <div className="dashboard-nav-container">
        {/* Brand Section */}
        <Link to={getDashboardPath()} className="dashboard-nav-brand">
          <img 
            src="/assets/moe-logo.jpg" 
            alt="MOE Athletics" 
            className="dashboard-nav-logo"
            onError={(e) => {
              (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Ccircle cx="50" cy="50" r="45" fill="%23C41E3A"/%3E%3Ctext x="50" y="60" text-anchor="middle" fill="white" font-size="24" font-weight="bold"%3EMOE%3C/text%3E%3C/svg%3E';
            }}
          />
          <div className="dashboard-nav-brand-text">
            <h1 className="dashboard-nav-title">Athletics</h1>
            <span className="dashboard-nav-subtitle">Regional Inter Secondary Schools</span>
          </div>
        </Link>

        {/* Navigation Menu */}
        <div className={`dashboard-nav-menu ${mobileMenuOpen ? 'open' : ''}`}>
          {navItems.map((item, index) => (
            <Link 
              key={index}
              to={item.path || '#'} 
              className={`dashboard-nav-link ${item.path && isActive(item.path) ? 'active' : ''}`}
            >
              {item.label}
            </Link>
          ))}
        </div>

        {/* User Actions */}
        <div className="dashboard-nav-actions">
          <div className="dashboard-nav-user-dropdown" ref={dropdownRef}>
            <button 
              className="dashboard-nav-user"
              onClick={() => setAccountDropdownOpen(!accountDropdownOpen)}
            >
              <div className="dashboard-nav-user-avatar">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z" />
                </svg>
              </div>
              <div className="dashboard-nav-user-info">
                <span className="dashboard-nav-user-name">
                  {user?.firstName || user?.email?.split('@')[0] || 'User'}
                </span>
                <span className="dashboard-nav-user-role">
                  {user?.role?.charAt(0).toUpperCase()}{user?.role?.slice(1)}
                </span>
              </div>
              <svg 
                className={`dropdown-arrow ${accountDropdownOpen ? 'open' : ''}`}
                width="12" 
                height="12" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            
            {/* Dropdown Menu */}
            {accountDropdownOpen && (
              <div className="dashboard-user-dropdown-menu">
                <Link 
                  to={getAccountPath()}
                  className="dropdown-item"
                  onClick={() => setAccountDropdownOpen(false)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  Manage Account
                </Link>
              </div>
            )}
          </div>
          
          <button 
            className="dashboard-nav-logout"
            onClick={handleLogout}
            title="Logout"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            <span className="logout-text">Logout</span>
          </button>
        </div>

        {/* Mobile Menu Toggle */}
        <button 
          className="dashboard-mobile-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {mobileMenuOpen ? (
              <path d="M18 6L6 18M6 6l12 12" />
            ) : (
              <path d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="dashboard-mobile-menu">
          {navItems.map((item, index) => (
            <Link 
              key={index}
              to={item.path || '#'} 
              className={`dashboard-mobile-link ${item.path && isActive(item.path) ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              {item.label}
            </Link>
          ))}
          <Link 
            to={getAccountPath()}
            className={`dashboard-mobile-link ${isActive(getAccountPath()) ? 'active' : ''}`}
            onClick={() => setMobileMenuOpen(false)}
          >
            Manage Account
          </Link>
          <div className="dashboard-mobile-user">
            <div className="dashboard-mobile-user-info">
              <span className="dashboard-mobile-user-name">
                {user?.firstName || user?.email?.split('@')[0] || 'User'}
              </span>
              <span className="dashboard-mobile-user-role">
                {user?.role?.charAt(0).toUpperCase()}{user?.role?.slice(1)}
              </span>
            </div>
            <button 
              className="dashboard-mobile-logout"
              onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Logout
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default DashboardHeader;
